import { ValueFormatterParams } from 'ag-grid-community';
import { useState } from 'react';
import { Col, Row } from 'reactstrap';
import PageTitle from '../page-title/page-title';
import { UseDateRangeHook, useGetRequestNewHook } from '../requestUtils';
import { GetCurrentCompany } from '../customer-context/customer-context';
import { DeleteContactsErrorDialog, SalesPersonOverview, UseSignalRHook, WebkuaTable } from '@frontend-workspace/webkua-lib';
import CompanyRequestsGrid from '../admin/company-requests-grid/company-requests-grid';
import { booleanHeader, Box, Container, CreateHeaders, currencyHeader, dateHeader, HeaderSizeEnum, IExistingOrderItemType, MasterDetailTable, OrderTypesEnum, Roles, SecondaryButton, SumRows, Tab, Tabs, textHeader } from '@frontend-workspace/react-shared-lib';
import { ISellerReport } from '../_models/seller-report';
import OverviewSkeleton from './overview-skeleton';
import TableDateSelection from 'libs/react-shared-lib/src/lib/focusTable/table-date-selection/table-date-selection';
import { addDays } from 'date-fns';
import { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import { useTranslation } from 'react-i18next';
import { GetRoles } from '../TokenContext';


/* eslint-disable-next-line */
export interface OverviewProps { }

export function Overview(props: OverviewProps) {
  const userRoles = GetRoles();
  if (userRoles.includes(Roles.Admin) || userRoles.includes(Roles.User)) {
    // if (userRole === Roles.Admin || Roles.User) { //TODO oppdater til salg når vi flytter over.
    return <SalesPersonOverview />;
  } else {
    return <OverviewContent />;
  }
}

export function OverviewContent() {
  const { t } = useTranslation();
  const currentCompany = GetCurrentCompany();
  const { startDate, endDate, onDateChange } = UseDateRangeHook(addDays(new Date(), -14), new Date());
  const [onlyActiveTrials, setOnlyActiveTrials] = useState<boolean>(true);

  const url = currentCompany ? `admin/${currentCompany?.number}/overview/${startDate}/${endDate}` : `admin/overview/${startDate}/${endDate}`;
  const dateRange: DateRange<Date> = [new Date(startDate), new Date(endDate)]
  const { data: sellerReport, isLoading, getData }: { data: ISellerReport, isLoading: boolean, getData: () => void } = useGetRequestNewHook(url, [], [currentCompany?.number, startDate, endDate]);
  UseSignalRHook(getData, [OrderTypesEnum.ActivateUnregisteredCompany]);

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [deleteContactsErrorId, setDeleteContactsErrorId] = useState<string | undefined>(undefined);

  const openDeleteContactsErrorDialog = (orderId: string) => setDeleteContactsErrorId(orderId);
  const closeDeleteContactsErrorDialog = () => setDeleteContactsErrorId(undefined);

  const navigateToTabOne = () => setSelectedTab(0);
  const navigateToTabTwo = () => setSelectedTab(1);
  const navigateToTabThree = () => setSelectedTab(2);
  const navigateToTabFour = () => setSelectedTab(3);
  const navigateToTabFive = () => setSelectedTab(4);
  const navigateToTabSix = () => setSelectedTab(5);
  const navigateToTabSeven = () => setSelectedTab(6);

  const setAsHandledRenderer = (params: ValueFormatterParams) => (
    <SecondaryButton
      variant={"outlined"}
      size='small'
      onClick={() => openDeleteContactsErrorDialog(params.value)}>
      {t("standard.setAsHandled")}
    </SecondaryButton>
  );
  const LicenseNumbersRenderer = (params: ValueFormatterParams) =>
    params.data.items.map((item: IExistingOrderItemType) => <div key={item.id}>
      {item.licenseNumber}
    </div>);

  const ProductNameRenderer = (params: ValueFormatterParams) =>
    params.data.items.map((item: IExistingOrderItemType) => <div key={item.id}>
      {item.productName}
    </div>);

  const ChangeDescriptionRenderer = (params: ValueFormatterParams) =>
    params.data.items.map((item: IExistingOrderItemType) => <div key={item.id}>
      {item.changeDescription}
    </div>);

  const reasonForSaleRenderer = (params: ValueFormatterParams) => t(`reasonsForSale.${params.value}`);

  return (
    <Container
      className="container-fluid pt-4 px-0"
      maxWidth={false}
      sx={{ paddingBottom: '50px' }}>
      <Row>
        <Col>
          <Box
            component="div"
            className="pb-5">
            <PageTitle title={t("standard.overview")} subtitle='' />
            {isLoading ? <OverviewSkeleton /> : <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px' }}>
                <Tabs
                  value={selectedTab}
                  variant="scrollable"
                  scrollButtons="auto">
                  <Tab label={` ${t("overview.failedLogins")} (${sellerReport.failedLogin.length})`} onClick={navigateToTabOne} />
                  <Tab label={`${t("overview.lastSales")} (${sellerReport.soldLicenses.length})`} onClick={navigateToTabTwo} />
                  {sellerReport.newCompanyRequests?.length > 0 &&
                    <Tab label={`${t("overview.newCompanyRequests")} (${sellerReport.newCompanyRequests.length})`} onClick={navigateToTabThree} value={2} />
                  }
                  {sellerReport.failedOrders?.length > 0 && <Tab label={`${t("overview.failedOrders")} (${sellerReport.failedOrders.length})`} onClick={navigateToTabFour} value={3} />}
                  {sellerReport.offlineRenewal?.length > 0 && <Tab label={`${t("overview.offlineLicensesRenewal")} (${sellerReport.offlineRenewal.length})`} onClick={navigateToTabFive} value={4} />}
                  {sellerReport.futureOrders?.length > 0 && <Tab label={`Fremtidige ordre (${sellerReport.futureOrders.length})`} onClick={navigateToTabSix} value={5} />}
                  {sellerReport.trialLicenses?.length > 0 && <Tab label={`Prøvelisenser (${sellerReport.trialLicenses.length})`} onClick={navigateToTabSeven} value={6} />}
                  {/* <Tab label={"Interesser uten produkt"} onClick={navigateToTabFive} />
                  <Tab label={"Lisenser som ikke brukes"} onClick={navigateToTabSix} /> */}
                </Tabs>
              </Box>
              <div className='d-flex flex-wrap flex-sm-nowrap mb-3'>
                {(selectedTab !== 5 && selectedTab !== 6) && <TableDateSelection
                  dateRange={dateRange}
                  onSetDateRange={onDateChange}
                  supportFutureDates={false}
                />}
              </div>
              {selectedTab === 0 &&
                <WebkuaTable
                  isLoading={isLoading}
                  headers={CreateHeaders([
                    textHeader("companyNumber", t("standard.companyNo"), HeaderSizeEnum.MEDIUM),
                    textHeader("companyName", t("standard.companyName"), HeaderSizeEnum.EXTRA_LARGE),
                    textHeader("licenseNumber", t("standard.licenseNumber"), HeaderSizeEnum.MEDIUM),
                    textHeader("productName", t("standard.product"), HeaderSizeEnum.EXTRA_LARGE),
                    textHeader("userEmail", t("standard.user"), HeaderSizeEnum.EXTRA_LARGE),
                    textHeader("personName", t("standard.name"), HeaderSizeEnum.EXTRA_LARGE),
                    booleanHeader("newLicensing", t("standard.newLicensingSystem")),
                    dateHeader("timeStamp", t("standard.timestamp")),
                    textHeader("reason", t("overview.reason"), HeaderSizeEnum.EXTRA_LARGE)
                  ])}
                  data={sellerReport.failedLogin}
                />
              }
              {selectedTab === 1 &&
                <WebkuaTable
                  headers={CreateHeaders([
                    textHeader("companyNumber", t("standard.companyNo"), HeaderSizeEnum.MEDIUM),
                    textHeader("companyName", t("standard.companyName"), HeaderSizeEnum.EXTRA_LARGE),
                    textHeader("contractNumber", t("standard.contract"), HeaderSizeEnum.MEDIUM),
                    textHeader("licenseNumber", t("standard.licenseNumber"), HeaderSizeEnum.MEDIUM),
                    textHeader("productName", t("standard.product"), HeaderSizeEnum.EXTRA_LARGE),
                    textHeader("numberOfSeats", t("standard.numberOfSeats"), HeaderSizeEnum.MEDIUM, { summary: SumRows }),
                    currencyHeader("renewalPrice", t("standard.renewalPrice"), HeaderSizeEnum.MEDIUM, { summary: SumRows }),
                    dateHeader("licenseCreatedDate", t("standard.created")),
                    booleanHeader("hasBeenUsed", t("overview.startedUsing"))
                  ])}
                  data={sellerReport.soldLicenses}
                />
              }
              {selectedTab === 2 &&
                <CompanyRequestsGrid companyRequests={sellerReport.newCompanyRequests} getCompanyRequests={getData} />
              }
              {selectedTab === 3 &&
                <WebkuaTable
                  headers={CreateHeaders([
                    textHeader("orderId", t("overview.orderID"), HeaderSizeEnum.LARGE),
                    textHeader("companyNumber", t("standard.companyNo"), HeaderSizeEnum.MEDIUM),
                    textHeader("companyName", t("standard.companyName"), HeaderSizeEnum.EXTRA_LARGE),
                    textHeader("contractNumber", t("standard.contract"), HeaderSizeEnum.MEDIUM),
                    textHeader("orderType", t("standard.type"), HeaderSizeEnum.LARGE),
                    dateHeader("activationDate", t("standard.date")),
                    textHeader("errorMessage", t("standard.errorText"), HeaderSizeEnum.EXTRA_LARGE),
                    textHeader("orderId", "", HeaderSizeEnum.EXTRA_LARGE, {
                      renderer: setAsHandledRenderer,
                      cellStyle: { justifyContent: 'right' },
                      headerClass: "ag-right-aligned-header"
                    })
                  ])}
                  data={sellerReport.failedOrders}
                />
              }
              {selectedTab === 4 &&
                <WebkuaTable
                  headers={CreateHeaders([
                    textHeader("companyName", t("standard.companyName"), HeaderSizeEnum.EXTRA_LARGE),
                    textHeader("companyNumber", t("standard.companyNo"), HeaderSizeEnum.MEDIUM),
                    textHeader("contractNumber", t("standard.contract"), HeaderSizeEnum.MEDIUM),
                    textHeader("licenseNumber", t("standard.licenseNumber"), HeaderSizeEnum.MEDIUM),
                    textHeader("daysUntilRenewal", t("overview.daysToRenewal"), HeaderSizeEnum.MEDIUM),
                    booleanHeader("isTerminated", t("overview.terminates")),
                    dateHeader("renewalDate", t("standard.date")),
                  ])}
                  data={sellerReport.offlineRenewal}
                />
              }
              {selectedTab === 5 &&
                <MasterDetailTable
                  masterHeaders={CreateHeaders([
                    textHeader("changedBy", "Endret av", HeaderSizeEnum.LARGE, { renderer: 'agGroupCellRenderer' }),
                    textHeader("id", "Ordre ID", HeaderSizeEnum.LARGE, { hide: true }),
                    dateHeader("activationDate", "Utførelsesdato"),
                    dateHeader("orderDate", "Bestillingsdato"),
                    textHeader("companyNumber", "Firmanr.", HeaderSizeEnum.SMALL),
                    textHeader("companyName", "Firmanavn", HeaderSizeEnum.EXTRA_LARGE),
                    textHeader("licenseNumber", "Lisensnr.", HeaderSizeEnum.SMALL, { renderer: LicenseNumbersRenderer }),
                    textHeader("productName", "Produkt", HeaderSizeEnum.EXTRA_LARGE, { renderer: ProductNameRenderer }),
                    textHeader("change", "Endring", HeaderSizeEnum.MEDIUM, { renderer: ChangeDescriptionRenderer }),
                    textHeader("contractResponsibleName", "Kontraktsansvarlig", HeaderSizeEnum.LARGE),
                    textHeader("contractResponsibleEmail", "E-post kontraktansvarlig", HeaderSizeEnum.LARGE),
                    textHeader("contractResponsiblePhone", "Tlf kontraktansvarlig", HeaderSizeEnum.MEDIUM),
                    currencyHeader("price", "Pris", HeaderSizeEnum.MEDIUM, {
                      cellStyle: { justifyContent: 'right' },
                      headerClass: "ag-right-aligned-header"
                    }),
                    textHeader("comment", "Kommentar", HeaderSizeEnum.EXTRA_LARGE),
                    textHeader("reasonForSale", "Grunn", HeaderSizeEnum.MEDIUM, { renderer: reasonForSaleRenderer }),
                  ])}
                  data={sellerReport.futureOrders}
                  detailHeaders={CreateHeaders([
                    textHeader("productName", "Beskrivelse", HeaderSizeEnum.EXTRA_LARGE),
                    textHeader("vismaContractItemId", "Visma ID", HeaderSizeEnum.SMALL),
                    textHeader("quantity", "Antall", HeaderSizeEnum.SMALL, {
                      cellStyle: { justifyContent: 'right' },
                      headerClass: "ag-right-aligned-header"
                    }),
                    currencyHeader("discount", "Rabatt", HeaderSizeEnum.SMALL, {
                      cellStyle: { justifyContent: 'right' },
                      headerClass: "ag-right-aligned-header"
                    }),
                    currencyHeader("salePrice", "Sum", HeaderSizeEnum.SMALL, {
                      cellStyle: { justifyContent: 'right' },
                      headerClass: "ag-right-aligned-header"
                    })
                  ])}
                  detailsPropertyName={"items"}
                  fitHeight
                />
              }
              {selectedTab === 6 && <WebkuaTable
                data={sellerReport.trialLicenses.filter(t => !onlyActiveTrials || t.isActive)}
                tableFilters={[
                  { label: t("standard.active"), onChange: () => setOnlyActiveTrials(!onlyActiveTrials), checked: onlyActiveTrials }
                ]}
                headers={CreateHeaders([
                  textHeader("companyNumber", t("standard.companyNo"), HeaderSizeEnum.MEDIUM),
                  textHeader("companyName", t("standard.companyName"), HeaderSizeEnum.EXTRA_LARGE),
                  textHeader("productNames", t("standard.product"), HeaderSizeEnum.EXTRA_LARGE),
                  textHeader("userEmails", t("standard.email"), HeaderSizeEnum.EXTRA_LARGE),
                  dateHeader("startDate", t("standard.startDate")),
                  dateHeader("endDate", t("standard.endDate")),
                  booleanHeader("isActive", t("standard.active")),

                ])}
              />}
            </>}
          </Box>
        </Col>
      </Row>
      {deleteContactsErrorId && <DeleteContactsErrorDialog
        open={!!deleteContactsErrorId}
        onClose={closeDeleteContactsErrorDialog}
        orderId={deleteContactsErrorId}
      />}
      {/* {selectedEmail && <UserAccountDetailsDialog
        email={selectedEmail}
        onClose={closeAccountDetails}
      />} */}
    </Container>
  );
}

export default Overview;